import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { Box, Button, Divider, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TodayIcon from '@material-ui/icons/Today';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

/**
 * Interface pour les props du composant OneDayStripSelect
 */
interface OneDayStripSelectProps {
  /** Date actuellement sélectionnée */
  value: Dayjs;
  /** Fonction appelée lors du changement de date */
  onChange: (value: Dayjs) => any;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(1),

    '& > *': {
      margin: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(1),
      marginBottom: 0,
      marginTop: 0,
      padding: theme.spacing(0.5, 1),
      paddingTop: 0,
      paddingBottom: 0
    },
  },
  btnCalendar: {
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(1),
      margin: 0,
      padding: 0
    },
  }
}));

/**
 * Composant interne pour afficher un jour dans la bande de sélection
 * 
 * @param date Label du jour à afficher
 * @param selected Indique si ce jour est sélectionné
 * @param onClick Fonction appelée lors du clic (optionnelle)
 */
const NextDay = ({
  date,
  selected,
  onClick,
}: {
  date: string;
  selected: boolean;
  onClick?: any;
}) => {
  const theme = useTheme();
  const isUnderSm = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <Button onClick={onClick} style={isUnderSm ? {margin:"0 0 0 0", padding:"0 0.5em 0 0.5em", minWidth: "auto"} : {}}>
      <Typography
        variant={isUnderSm ? "subtitle1" : "h5"}
        gutterBottom
        color={selected ? 'textPrimary' : 'textSecondary'}
        style={{ opacity: selected ? 1 : 0.7, whiteSpace:"nowrap" }}
      >
        {date}
      </Typography>
    </Button>
  );
};
/**
 * Composant de sélection de jour avec navigation rapide
 * 
 * Ce composant affiche :
 * - Un sélecteur de date avec calendrier
 * - Navigation rapide vers les jours précédent/suivant
 * - Labels intelligents (Hier, Aujourd'hui, Demain, etc.)
 * - Design responsive pour mobile et desktop
 * 
 * Logique métier :
 * - Affichage de labels relatifs pour les jours proches
 * - Format DD MM YYYY pour les dates éloignées
 * - Navigation par clic sur les boutons de jour
 * - Ouverture du calendrier via l'icône Today
 * 
 * @param value Date actuellement sélectionnée
 * @param onChange Fonction appelée lors du changement de date
 * @returns Composant React de sélection de jour
 */
export const OneDayStripSelect = ({
  value,
  onChange,
}: OneDayStripSelectProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const today = dayjs();
  const theme = useTheme();
  const isUnderSm = useMediaQuery(theme.breakpoints.down('xs'));
  const yesterday = today.subtract(1, 'day');
  const tomorrow = today.add(1,'day');
  const intwodays = today.add(2,'day');

  const minusOne = value.subtract(1, 'day');
  const plusOne = value.add(1, 'day');
  const plusTwo = value.add(2, 'day');

  function getDayLabelFromDayjs(d: dayjs.Dayjs) {
    if(d.isSame(yesterday, 'day')) {
      return 'Hier';
    } else
    if(d.isSame(today, 'day')) {
      return 'Aujourd\'hui';
    } else if(d.isSame(tomorrow, 'day')) {
      return 'Demain';
    } else if(d.isSame(intwodays, 'day')) {
      return 'Après-demain';
    }
    return d.format('DD MM YYYY');

  }

  return (
    <Box display={"flex"} className={classes.root}>
      <Button size={'small'} onClick={() => setIsOpen(true)} className={classes.btnCalendar}>
        <TodayIcon />
      </Button>
      <KeyboardDatePicker
        autoOk
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        variant="inline"
        inputVariant={!isUnderSm ? "outlined" : "standard"}
        label="With keyboard"
        format="MM/dd/yyyy"
        value={today}
        style={{ width: '0px', visibility: 'hidden' }}
        InputAdornmentProps={{ position: 'start', disableTypography: true }}
        onChange={onChange as (date: MaterialUiPickersDate) => void}
      />
      <NextDay
        date={getDayLabelFromDayjs(minusOne)}
        selected={false}
        onClick={() => onChange(minusOne)}
      />
      {!isUnderSm && <Divider orientation="vertical" flexItem />}
      <NextDay
        date={getDayLabelFromDayjs(value)}
        selected={true} />
      {!isUnderSm && <Divider orientation="vertical" flexItem />}
      <NextDay
        date={getDayLabelFromDayjs(plusOne)}
        selected={false}
        onClick={() => onChange(plusOne)}
      />
      {!isUnderSm && <Divider orientation="vertical" flexItem />}
      <NextDay
        date={getDayLabelFromDayjs(plusTwo)}
        selected={false}
        onClick={() => onChange(plusTwo)}
      />
    </Box>
  );
};
